import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db, storage } from '../lib/firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { updateProfile } from 'firebase/auth';
import { User, Edit2, Save, X, Facebook, Instagram, Camera } from 'lucide-react';
import XLogo from './icons/XLogo';
import TikTokLogo from './icons/TikTokLogo';

interface UserProfile {
  username: string;
  bio: string;
  userId: string;
  photoURL?: string;
  socialLinks: {
    facebook?: string;
    twitter?: string;
    instagram?: string;
    tiktok?: string;
  };
}

interface EmailChangeModal {
  isOpen: boolean;
  newEmail: string;
  password: string;
  verificationSent: boolean;
}

export default function Profile() {
  const { user, loading: authLoading, updateEmail } = useAuth();
  const { userId } = useParams();
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [emailModal, setEmailModal] = useState<EmailChangeModal>({
    isOpen: false,
    newEmail: '',
    password: '',
    verificationSent: false
  });
  const [profile, setProfile] = useState<UserProfile>({ 
    username: '', 
    bio: '', 
    userId: '',
    socialLinks: {} 
  });
  const [editedProfile, setEditedProfile] = useState<UserProfile>({ 
    username: '', 
    bio: '', 
    userId: '',
    socialLinks: {} 
  });
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Function to clean and format social media links
  const formatSocialLink = (url: string, type: 'facebook' | 'twitter' | 'instagram' | 'tiktok') => {
    if (!url) return '';
    
    // Remove protocol if present
    let formattedUrl = url.replace(/^https?:\/\//, '');
    
    // Add domain if not present
    if (type === 'facebook' && !formattedUrl.startsWith('facebook.com') && !formattedUrl.startsWith('www.facebook.com')) {
      formattedUrl = `facebook.com/${formattedUrl}`;
    } else if (type === 'twitter' && !formattedUrl.startsWith('x.com') && !formattedUrl.startsWith('twitter.com') && !formattedUrl.startsWith('www.twitter.com') && !formattedUrl.startsWith('www.x.com')) {
      formattedUrl = `x.com/${formattedUrl}`;
    } else if (type === 'instagram' && !formattedUrl.startsWith('instagram.com') && !formattedUrl.startsWith('www.instagram.com')) {
      formattedUrl = `instagram.com/${formattedUrl}`;
    } else if (type === 'tiktok' && !formattedUrl.startsWith('tiktok.com') && !formattedUrl.startsWith('www.tiktok.com')) {
      formattedUrl = `tiktok.com/@${formattedUrl}`;
    }
    
    // Add protocol
    return `https://${formattedUrl}`;
  };

  useEffect(() => {
    if (!userId || !db) {
      setLoading(false);
      return;
    }

    console.log('Setting up profile for user:', userId);
    setLoading(true);
    setError(null);

    const getProfile = async () => {
      try {
        const userDoc = await getDoc(doc(db, 'users', userId));
        if (userDoc.exists()) {
          const userData = userDoc.data() as UserProfile;
          setProfile(userData);
          setEditedProfile(userData);
        } else {
          setError('Profile not found');
        }
      } catch (err) {
        console.error('Error fetching profile:', err);
        setError('Failed to load profile');
      }
      setLoading(false);
    };

    getProfile();
  }, [userId, db, user]);

  const handleProfileUpdate = async () => {
    if (!user) {
      setError('You must be logged in to update your profile');
      return;
    }

    // Check if the logged-in user is trying to edit their own profile
    if (user.uid !== userId) {
      setError('You can only edit your own profile');
      return;
    }

    if (!editedProfile.username?.trim()) {
      setError('Username is required');
      return;
    }

    try {
      const userRef = doc(db, 'users', userId);
      
      // First check if the document exists
      const docSnap = await getDoc(userRef);
      if (!docSnap.exists()) {
        setError('Profile document not found');
        return;
      }

      const updateData = {
        username: editedProfile.username.trim(),
        bio: editedProfile.bio?.trim() || '',
        userId: userId,
        socialLinks: editedProfile.socialLinks || {}
      };

      await updateDoc(userRef, updateData);
      setProfile(editedProfile);
      setEditing(false);
      setError(null);
    } catch (err) {
      console.error('Error updating profile:', err);
      const error = err as Error;
      setError(`Failed to update profile: ${error.message}`);
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;

    try {
      setUploading(true);
      setError(null);

      // Check if the logged-in user is trying to edit their own profile
      if (user.uid !== userId) {
        setError('You can only edit your own profile photo');
        return;
      }

      // Upload image to Firebase Storage
      const storageRef = ref(storage, `profile-pictures/${user.uid}/${file.name}`);
      await uploadBytes(storageRef, file);
      const photoURL = await getDownloadURL(storageRef);

      // Update auth profile
      await updateProfile(user, { photoURL });

      // Update Firestore profile
      await updateDoc(doc(db, 'users', userId), {
        photoURL
      });

      setProfile({ ...profile, photoURL });
      setEditedProfile({ ...editedProfile, photoURL });
    } catch (err) {
      console.error('Error uploading profile picture:', err);
      setError('Failed to upload profile picture');
    } finally {
      setUploading(false);
    }
  };

  const handleEmailChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    
    try {
      await updateEmail(emailModal.newEmail, emailModal.password);
      setEmailModal(prev => ({ ...prev, verificationSent: true }));
      setSuccess('A verification email has been sent to your new email address. Please check your new email and follow the instructions to verify and complete the change.');
    } catch (err: any) {
      setError(err.message || 'Failed to update email');
    }
  };

  if (authLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          <p>Please log in to view your profile and messages.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-0 md:p-6">
      {error && (
        <div className="bg-red-500/10 border border-red-500 text-red-500 px-4 py-3 rounded-lg relative mb-4">
          <p>{error}</p>
          <button 
            onClick={() => setError(null)}
            className="absolute top-0 right-0 p-4"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {emailModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              Change Email Address
            </h3>
            {emailModal.verificationSent ? (
              <div className="space-y-4">
                <p className="text-gray-600">
                  A verification email has been sent to {emailModal.newEmail}. 
                  Please check your email and click the verification link to complete the change.
                </p>
                <button
                  onClick={() => setEmailModal({ isOpen: false, newEmail: '', password: '', verificationSent: false })}
                  className="w-full px-4 py-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200"
                >
                  Close
                </button>
              </div>
            ) : (
              <form onSubmit={handleEmailChange} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    New Email Address
                  </label>
                  <input
                    type="email"
                    value={emailModal.newEmail}
                    onChange={(e) => setEmailModal(prev => ({ ...prev, newEmail: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Current Password
                  </label>
                  <input
                    type="password"
                    value={emailModal.password}
                    onChange={(e) => setEmailModal(prev => ({ ...prev, password: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                    required
                    placeholder="Enter your password to confirm"
                  />
                </div>
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setEmailModal({ isOpen: false, newEmail: '', password: '', verificationSent: false })}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800 bg-gray-100 rounded-lg hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600"
                  >
                    Send Verification
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-xl p-8 border border-gray-200">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-orange-500">Profile</h2>
          {user && user.uid === userId && (
            <div className="flex space-x-2">
              {!editing ? (
                <button
                  onClick={() => setEditing(true)}
                  className="flex items-center text-orange-600 hover:text-orange-700"
                >
                  <Edit2 className="h-4 w-4 mr-1" />
                  Edit
                </button>
              ) : (
                <div className="flex space-x-2">
                  <button
                    onClick={handleProfileUpdate}
                    className="flex items-center text-green-600 hover:text-green-700"
                  >
                    <Save className="h-4 w-4 mr-1" />
                    Save
                  </button>
                  <button
                    onClick={() => {
                      setEditing(false);
                      setEditedProfile(profile);
                    }}
                    className="flex items-center text-red-600 hover:text-red-700"
                  >
                    <X className="h-4 w-4 mr-1" />
                    Cancel
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="space-y-6">
          <div className="flex justify-center">
            <div className="relative">
              <div className="h-32 w-32 rounded-full bg-navy-500 flex items-center justify-center overflow-hidden">
                {profile.photoURL ? (
                  <img 
                    src={profile.photoURL} 
                    alt={profile.username} 
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <User size={64} className="text-orange-500" />
                )}
              </div>
              {user && user.uid === userId && (
                <>
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileSelect}
                    accept="image/*"
                    className="hidden"
                  />
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="absolute bottom-0 right-0 p-2 bg-orange-500 rounded-full text-white hover:bg-orange-400 transition-colors"
                    disabled={uploading}
                  >
                    {uploading ? (
                      <div className="h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <Camera className="h-5 w-5" />
                    )}
                  </button>
                </>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-orange-500">Username</label>
            {editing ? (
              <input
                type="text"
                value={editedProfile.username}
                onChange={(e) => setEditedProfile({ ...editedProfile, username: e.target.value })}
                className="w-full bg-gray-50 border border-gray-200 rounded-lg px-4 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            ) : (
              <p className="bg-gray-50 border border-gray-200 rounded-lg text-black px-4 py-2">{profile.username}</p>
            )}
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-orange-500">Bio</label>
            {editing ? (
              <textarea
                value={editedProfile.bio}
                onChange={(e) => setEditedProfile({ ...editedProfile, bio: e.target.value })}
                rows={4}
                className="w-full bg-gray-50 border border-gray-200 rounded-lg px-4 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              />
            ) : (
              <p className="bg-gray-50 border border-gray-200 rounded-lg text-black px-4 py-2 min-h-[40px]">{profile.bio || 'No bio yet'}</p>
            )}
          </div>

          <div className="space-y-4">
            <h3 className="text-lg font-medium text-orange-500">Social Media Links</h3>
            
            <div className="space-y-4">
              {/* Facebook */}
              <div className="flex items-center gap-2">
                <Facebook className="text-[#1877F2] w-6 h-6" />
                {editing ? (
                  <input
                    type="text"
                    value={editedProfile.socialLinks?.facebook || ''}
                    onChange={(e) => setEditedProfile({
                      ...editedProfile,
                      socialLinks: { ...editedProfile.socialLinks, facebook: e.target.value }
                    })}
                    placeholder="Enter your Facebook profile URL"
                    className="flex-1 bg-gray-50 border border-gray-200 rounded-lg px-4 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                ) : profile.socialLinks?.facebook ? (
                  <a 
                    href={profile.socialLinks.facebook}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-gray-50 border border-gray-200 rounded-lg text-blue-600 hover:text-blue-800 px-4 py-2 flex-1 break-all overflow-hidden text-ellipsis"
                  >
                    {profile.socialLinks.facebook}
                  </a>
                ) : (
                  <p className="bg-gray-50 border border-gray-200 rounded-lg text-gray-500 px-4 py-2 flex-1">No Facebook profile linked</p>
                )}
              </div>

              {/* X */}
              <div className="flex items-center gap-2">
                <XLogo className="text-black w-6 h-6" />
                {editing ? (
                  <input
                    type="text"
                    value={editedProfile.socialLinks?.twitter || ''}
                    onChange={(e) => setEditedProfile({
                      ...editedProfile,
                      socialLinks: { ...editedProfile.socialLinks, twitter: e.target.value }
                    })}
                    className="flex-1 bg-gray-50 border border-gray-200 rounded-lg px-4 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                ) : profile.socialLinks?.twitter ? (
                  <a 
                    href={formatSocialLink(profile.socialLinks.twitter, 'twitter')}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-gray-50 border border-gray-200 rounded-lg text-blue-600 hover:text-blue-800 px-4 py-2 flex-1 break-all overflow-hidden text-ellipsis"
                  >
                    {formatSocialLink(profile.socialLinks.twitter, 'twitter')}
                  </a>
                ) : (
                  <p className="bg-gray-50 border border-gray-200 rounded-lg text-gray-500 px-4 py-2 flex-1">No X profile linked</p>
                )}
              </div>

              {/* Instagram */}
              <div className="flex items-center gap-2">
                <Instagram className="text-[#E4405F] w-6 h-6" />
                {editing ? (
                  <input
                    type="text"
                    value={editedProfile.socialLinks?.instagram || ''}
                    onChange={(e) => setEditedProfile({
                      ...editedProfile,
                      socialLinks: { ...editedProfile.socialLinks, instagram: e.target.value }
                    })}
                    className="flex-1 bg-gray-50 border border-gray-200 rounded-lg px-4 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                ) : profile.socialLinks?.instagram ? (
                  <a 
                    href={formatSocialLink(profile.socialLinks.instagram, 'instagram')}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-gray-50 border border-gray-200 rounded-lg text-blue-600 hover:text-blue-800 px-4 py-2 flex-1 break-all overflow-hidden text-ellipsis"
                  >
                    {formatSocialLink(profile.socialLinks.instagram, 'instagram')}
                  </a>
                ) : (
                  <p className="bg-gray-50 border border-gray-200 rounded-lg text-gray-500 px-4 py-2 flex-1">No Instagram profile linked</p>
                )}
              </div>

              {/* TikTok */}
              <div className="flex items-center gap-2">
                <TikTokLogo className="w-6 h-6" />
                {editing ? (
                  <input
                    type="text"
                    value={editedProfile.socialLinks?.tiktok || ''}
                    onChange={(e) => setEditedProfile({
                      ...editedProfile,
                      socialLinks: { ...editedProfile.socialLinks, tiktok: e.target.value }
                    })}
                    className="flex-1 bg-gray-50 border border-gray-200 rounded-lg px-4 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="@username or full URL"
                  />
                ) : profile.socialLinks?.tiktok ? (
                  <a 
                    href={formatSocialLink(profile.socialLinks.tiktok, 'tiktok')}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-gray-50 border border-gray-200 rounded-lg text-blue-600 hover:text-blue-800 px-4 py-2 flex-1 break-all overflow-hidden text-ellipsis"
                  >
                    {formatSocialLink(profile.socialLinks.tiktok, 'tiktok')}
                  </a>
                ) : (
                  <p className="bg-gray-50 border border-gray-200 rounded-lg text-gray-500 px-4 py-2 flex-1">No TikTok profile linked</p>
                )}
              </div>
            </div>
          </div>

          {editing && user?.uid === userId && (
            <div className="mt-6 p-4 bg-white rounded-lg shadow">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Email Settings</h3>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600">Current Email: {user?.email}</p>
                  <p className="text-sm text-gray-500">
                    {user?.emailVerified ? 'Verified' : 'Not verified'}
                  </p>
                </div>
                <button
                  onClick={() => setEmailModal({ isOpen: true, newEmail: '', password: '', verificationSent: false })}
                  className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600"
                >
                  Change Email
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {success && (
        <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg text-green-600">
          {success}
        </div>
      )}
      {error && (
        <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-600">
          {error}
        </div>
      )}
    </div>
  );
}